/**
 * Shared past-cycles list logic — used by AllPastCyclesScreen and the
 * PastCyclesModal so the closed-cycle filtering, per-type numbering and
 * outcome derivation live in one place.
 */
import { useMemo } from "react";
import {
  CheckCircle,
  MinusCircle,
  XCircle,
  Clock,
  Snowflake,
} from "lucide-react-native";
import {
  useCycles,
  CYCLE_TYPE_LABELS,
  CYCLE_TYPE_LABEL_KEYS,
  type Cycle,
  type CycleType,
} from "@src/packages/cycles/hooks/useCycles";
import { useTranslations } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import { formatDateTime } from "@src/packages/i18n/formatters";
import { EMBER } from "../../onboarding/theme";

export type CycleOutcome =
  "positive" | "negative" | "cancelled" | "completed" | "ongoing" | "frozen";

export const OUTCOME_COLORS: Record<CycleOutcome, string> = {
  positive: EMBER.colors.green,
  negative: EMBER.colors.textLight,
  cancelled: EMBER.colors.warm,
  completed: EMBER.colors.textMid,
  ongoing: EMBER.colors.warm,
  frozen: EMBER.colors.cryo,
};

export const OUTCOME_LABEL_KEYS: Record<CycleOutcome, string> = {
  positive: "care.all_past_cycles.outcome_positive",
  negative: "care.all_past_cycles.outcome_negative",
  cancelled: "care.all_past_cycles.outcome_cancelled",
  completed: "care.all_past_cycles.outcome_completed",
  ongoing: "care.all_past_cycles.outcome_ongoing",
  frozen: "care.all_past_cycles.outcome_frozen",
};

export const OUTCOME_ICONS: Record<CycleOutcome, typeof CheckCircle> = {
  positive: CheckCircle,
  negative: MinusCircle,
  cancelled: XCircle,
  completed: CheckCircle,
  ongoing: Clock,
  frozen: Snowflake,
};

export type PastCycleListItem = {
  id: string;
  cycleType: string;
  cycleNumber: number;
  startDate: string;
  endDate?: string;
  clinic?: string;
  outcome: CycleOutcome;
};

type CycleWithMeta = Cycle & {
  createdAt?: string;
  clinic?: string | null;
  provider?: string | null;
  outcome?: { type?: string };
};

export function usePastCyclesList(): PastCycleListItem[] {
  const t = useTranslations();
  const { locale } = useLocale();
  const { data: allCycles } = useCycles();

  return useMemo(() => {
    if (!allCycles) return [];
    const closed = allCycles.filter((c) => c.status === "closed");
    // Sort by createdAt ascending so cycle numbers match usePastCycleDetail
    const sorted = [...closed].sort((a, b) => {
      const ac = (a as CycleWithMeta).createdAt ?? "";
      const bc = (b as CycleWithMeta).createdAt ?? "";
      return new Date(ac).getTime() - new Date(bc).getTime();
    });
    const typeCounters: Record<string, number> = {};
    return sorted.map((c) => {
      typeCounters[c.cycleType] = (typeCounters[c.cycleType] ?? 0) + 1;
      const typeLabelKey = CYCLE_TYPE_LABEL_KEYS[c.cycleType as CycleType];
      const typeLabel = typeLabelKey
        ? t(typeLabelKey)
        : (CYCLE_TYPE_LABELS[c.cycleType] ?? c.cycleType);

      const outcome: CycleOutcome = (() => {
        const ot = (c as CycleWithMeta).outcome?.type;
        if (ot === "cancelled") return "cancelled";
        if (ot === "pregnancy") return "positive";
        if (ot === "no_pregnancy") return "negative";
        if (ot === "miscarriage") return "negative";

        const match = c.description?.match(/^\[outcome:(\w+)\]/);
        if (match) {
          const tag = match[1];
          if (tag === "cancelled") return "cancelled";
          if (tag === "pregnancy") return "positive";
          if (tag === "no_pregnancy" || tag === "miscarriage") {
            return "negative";
          }
        }
        return "completed";
      })();

      return {
        id: c.id,
        cycleType: typeLabel,
        cycleNumber: typeCounters[c.cycleType]!,
        startDate: c.startDate
          ? formatDateTime(c.startDate, locale, {
              month: "short",
              year: "numeric",
            })
          : "",
        endDate: c.endDate
          ? formatDateTime(c.endDate, locale, {
              month: "short",
              year: "numeric",
            })
          : undefined,
        clinic:
          (c as CycleWithMeta).clinic ??
          (c as CycleWithMeta).provider ??
          undefined,
        outcome,
      };
    });
  }, [allCycles, t, locale]);
}
